import { ff } from 'helpers/featureFlags';
import cloneDeep from 'lodash/cloneDeep';
import DebugRender from 'components/Debug/Render';
import { ascii, globalFilter } from 'constants/patterns';
import React from 'react';
import { OnError, OnUpdate } from 'types/props';
import { FeatureFlag } from '@redux/modules/featureFlags/reducer';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { FieldModel } from 'forms/shared/types';
import ValidationElement from '../ValidationElement';
import { ariaLabel } from '../Generic';
import { stateFormErrorConnector } from '../shared/stateFormErrors';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

interface UpdateProps {
  name?: string
  value?: string
}

interface Props extends Partial<FieldModel>, ValidationElementProps {
  name: string
  value: string
  focus: boolean
  error: boolean
  valid: boolean
  useFilter: boolean
  minlength: number
  maxlength: number
  spellcheck: boolean
  autocapitalize: boolean
  autocorrect: boolean
  autocomplete: string
  features: FeatureFlag[]
  onError: OnError
  onUpdate: OnUpdate<UpdateProps>
  hasStateFormError: boolean
  textarea: React.RefObject<HTMLInputElement>
}

interface ComponentState {
  uid: string
  focus: boolean
  error: boolean
  valid: boolean
  value: string
  maxlength: number
}

export interface ITextarea {
  clearValue: () => void
}

class Textarea extends ValidationElement<Props, ComponentState> {
  static defaultProps: Partial<Props> = {
    name: 'textarea',
    value: '',
    focus: false,
    error: false,
    valid: false,
    useFilter: true,
    minlength: 0,
    maxlength: 4000,
    spellcheck: true,
    autocapitalize: true,
    autocorrect: true,
    autocomplete: 'off',
    features: [],
    onError: () => [],
    onUpdate: () => {},
  };

  textarea = React.createRef<HTMLInputElement>();

  static errors: {
    code: string,
    func: (value: string, props: { required: boolean, minlength: string, maxlength: string, pattern: RegExp }) => boolean|null
  }[] = [
      {
        code: 'required',
        func: (value, props) => {
          if (props.required) {
            return !!value;
          }
          return true;
        },
      },
      {
        code: 'length',
        func: (value, props) => {
          if (!value || !value.length) {
            return null;
          }
          return (
            value.length >= parseInt(props.minlength || '0', 10)
        && value.length <= parseInt(props.maxlength || '4000', 10)
          );
        },
      },
      {
        code: 'pattern',
        func: (value, props) => {
          if (!value || !value.length) {
            return null;
          }
          const re = new RegExp(props.pattern);
          return re.test(value);
        },
      },
    ];

  constructor(props: Props) {
    super(props);

    this.state = {
      uid: `${props.name}-${super.guid()}`,
      focus: props.focus,
      error: props.error,
      valid: props.valid,
      value: props.value,
      maxlength: props.maxlength,
    };
  }

  /**
   * This allows us to switch logic of "hasError" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  hasError = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return this.props.hasStateFormError;
    }
    return this.state.error;
  };

  /**
   * This allows us to switch logic of "isValid" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  isValid = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return !this.props.hasStateFormError && (this.textarea?.current?.value || '').trim().length > 0;
    }
    return this.state.valid;
  };

  /**
   * Filters input values from a string by removing
   * all characters matched by the provided regex.
   *
   * @param {string} value
   * @param {RegExp} regex
   * @returns {string}
   */
  filterInput = (value: string, regex: RegExp): string => {
    let newValue = value;

    if (regex?.test(newValue)) {
      newValue = newValue.replace(regex, '');
    }

    return newValue;
  };

  handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newEvent = cloneDeep(event);
    let newValue: string = newEvent.target.value || '';

    newValue = this.filterInput(newValue, ascii);
    newValue = this.filterInput(newValue, globalFilter);

    // Enforce max length (covers copy+paste)
    if (newValue.length > this.state.maxlength) {
      newValue = newValue.slice(0, this.state.maxlength);
    }

    newEvent.target.value = newValue;

    this.setState(
      {
        value: newEvent.target.value,
      },
      () => {
        super.handleChange(newEvent);
      }
    );
  };

  handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    this.setState({ focus: true }, () => {
      super.handleFocus(event);
    });
  };

  handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const currentValue = this.props.useFilter ? this.onBlurFilter(event.target.value) : event.target.value;
    this.setState({ focus: false, value: currentValue });
    this.props.onUpdate?.({
      name: this.props.name,
      value: currentValue,
    });
    super.handleBlur(event);
  };

  /**
  * Apply filter on blur to clean up unnecessary text such as leading and trailing whitespace
  * @param value string to trim
  * @returns Trimmed string
  */
  onBlurFilter = (value: string): string => value.trim();

  /**
   * Execute validation checks on the value.
   */
  handleValidation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = `${this.props.value}`.trim();
    const errors = this.props.onError(
      value,
      this.constructor.errors.map((err) => ({
        code: err.code,
        valid: err.func(value, this.props),
        uid: this.state.uid,
      })) as ErrorCode[]
    ) || [];

    this.setState({
      error: errors.some((x) => x.valid === false),
      valid: errors.every((x) => x.valid === true),
    });
  };

  /**
   * Style classes applied to the wrapper.
   */
  divClass() {
    let klass = this.props.className || '';

    if (this.hasError()) {
      klass += ' usa-input-error';
    }

    return klass.trim();
  }

  /**
   * Style classes applied to the label element.
   */
  labelClass() {
    let klass = '';

    if (this.hasError()) {
      klass += ' usa-input-error-label';
    }

    return klass.trim();
  }

  /**
   * Style classes applied to the input element.
   */
  inputClass() {
    let klass = '';

    if (this.state.focus) {
      klass += ' usa-input-focus';
    }

    if (this.isValid()) {
      klass += ' usa-input-success';
    }

    return klass.trim();
  }

  clearValue = (withUpdate) => {
    this.setState({ value: '' }, () => {
      if (withUpdate) {
        this.props.onUpdate({
          name: this.props.name,
          value: '',
        });
      }
    });
  };

  render() {
    return (
      <div className={`hide-for-print ${this.divClass()}`}>
        <label className={this.labelClass()} htmlFor={this.state.uid}>
          {this.props.label}
        </label>
        <DebugRender parent={this} />
        <textarea
          className={this.inputClass()}
          id={this.state.uid}
          name={this.props.name}
          aria-label={this.props.label || ariaLabel(this.textarea.current)}
          disabled={this.props.disabled}
          maxLength={this.props.maxlength}
          pattern={this.props.pattern}
          readOnly={this.props.readonly}
          autoCapitalize={this.props.autocapitalize ? 'on' : 'off'}
          autoCorrect={this.props.autocorrect ? 'on' : 'off'}
          autoComplete={this.props.autocomplete ? 'on' : 'off'}
          spellCheck={this.props.spellcheck}
          required={this.props.required}
          value={this.state.value}
          onChange={this.handleChange} // eslint-disable-line @typescript-eslint/unbound-method
          onFocus={this.handleFocus} // eslint-disable-line @typescript-eslint/unbound-method
          onBlur={this.handleBlur} // eslint-disable-line @typescript-eslint/unbound-method
          ref={this.textarea}
        />
        <div className="textarea-print print-only">{this.props.value}</div>
      </div>
    );
  }
}

export default stateFormErrorConnector(Textarea);
